/**
 * The Hollow Court enemy family. Melee-first (design rule): watcher is the
 * ONLY ranged regular. Threat profiles:
 * - shambler: melee chaser pressure
 * - watcher: keeps range, aimed volleys (the lone shooter)
 * - husk: telegraphed lunge dashes
 * - chanter: slow ground slams, area denial
 * - splitter: slow blob; bursts into 2 mites on death (World.burstSplitter)
 * - mite: tiny fast chaser, splitter spawn only (never in wave/elite pools)
 * - brute: big slow bruiser, huge telegraphed slam zone
 * - flanker: orbits at mid range, then commits a telegraphed side-dash
 * - leech: fragile runner that pulse-heals nearby enemies (priority target)
 * - keeper: the vault ("Reliquary") minor boss. NEVER type 'boss' — that
 *   type triggers the run win; the keeper is loot-and-altar gate only.
 * - goblin: rare treasure-goblin prey — flees when engaged, escapes on a
 *   timer, never in wave/elite/enrage pools (spawned only by the World ctor).
 */
export type EnemyType =
  | 'shambler' | 'watcher' | 'husk' | 'chanter'
  | 'splitter' | 'mite' | 'brute' | 'flanker' | 'leech'
  | 'boss' | 'keeper' | 'goblin';

/** Elite affix modifiers (design §4.4). */
export type EliteAffix = 'fast' | 'shielded' | 'gemRich' | 'curseTouched';
export const ELITE_AFFIXES: readonly EliteAffix[] = ['fast', 'shielded', 'gemRich', 'curseTouched'];

export interface EnemyDef {
  hp: number;
  speed: number;
  radius: number;
  contactDamage: number;
  gemValue: number;
  /** Library pattern id fired on a cycle, if any. */
  firePattern: string | null;
  fireCooldown: number; // seconds between pattern casts
  /** Runner gate range, px; defaults to FIRE_RANGE (680). Slammers set less. */
  fireRange?: number;
}

export const ENEMY_DEFS: Record<EnemyType, EnemyDef> = {
  shambler: {
    hp: 18, speed: 95, radius: 14, contactDamage: 1, gemValue: 3,
    firePattern: null, fireCooldown: 0,
  },
  watcher: {
    hp: 14, speed: 80, radius: 13, contactDamage: 1, gemValue: 4,
    firePattern: 'watcherVolley', fireCooldown: 3.2,
  },
  husk: {
    hp: 26, speed: 75, radius: 16, contactDamage: 1, gemValue: 5,
    firePattern: null, fireCooldown: 0,
  },
  chanter: {
    hp: 20, speed: 55, radius: 15, contactDamage: 1, gemValue: 6,
    firePattern: 'chanterSlam', fireCooldown: 2.2, fireRange: 240,
  },
  splitter: {
    hp: 30, speed: 50, radius: 17, contactDamage: 1, gemValue: 6,
    firePattern: null, fireCooldown: 0,
  },
  mite: {
    hp: 5, speed: 130, radius: 8, contactDamage: 1, gemValue: 1,
    firePattern: null, fireCooldown: 0,
  },
  brute: {
    hp: 46, speed: 42, radius: 20, contactDamage: 1, gemValue: 8,
    firePattern: 'bruteSlam', fireCooldown: 3.2, fireRange: 260,
  },
  flanker: {
    hp: 16, speed: 120, radius: 13, contactDamage: 1, gemValue: 5,
    firePattern: null, fireCooldown: 0,
  },
  leech: {
    hp: 10, speed: 110, radius: 11, contactDamage: 1, gemValue: 4,
    firePattern: null, fireCooldown: 0,
  },
  boss: {
    hp: 850, speed: 60, radius: 34, contactDamage: 2, gemValue: 120,
    firePattern: null, fireCooldown: 0, // phases drive its runner externally
  },
  keeper: {
    hp: 170, speed: 45, radius: 24, contactDamage: 1, gemValue: 60,
    firePattern: null, fireCooldown: 0, // vault mode attaches its runner
  },
  goblin: {
    // Prey, not threat: contactDamage 0 (collideContact skips 0-contact
    // enemies — a bump must never eat a Wardstone charge). Speed sits just
    // under player base (260) so slows are the catch levers.
    hp: 200, speed: 240, radius: 14, contactDamage: 0, gemValue: 90,
    firePattern: null, fireCooldown: 0,
  },
};

// ---- Treasure goblin tuning (2026-07-28). Existence/loot are seeded (the
// 'goblin' stream, World ctor); behavior is gameplayRng like everything else.
export const GOBLIN_MIN_FLOOR = 3;
export const GOBLIN_CHANCE = 0.08;
/** Proximity that spooks a calm goblin (first damage also triggers). */
export const GOBLIN_AGGRO_RADIUS = 260;
/** Panic survival time — outlast it and the goblin escapes, loot lost. */
export const GOBLIN_PANIC_SECONDS = 20;
/** Calm-wander speed fraction of def speed. */
export const GOBLIN_WANDER_SPEED = 0.35;

/** Second-artifact chance. Floor/tier move the VALUE, never seeded draws. */
export function goblinSecondDropChance(floor: number, tier: number): number {
  return Math.min(0.30, 0.06 + 0.04 * (floor - GOBLIN_MIN_FLOOR) + 0.02 * tier);
}

/** Simple per-floor scaling (tuned in P9). */
export function enemyHpMult(floor: number): number {
  return 1 + 0.4 * (floor - 1);
}

export function spawnDensityMult(floor: number): number {
  return 1 + 0.25 * (floor - 1);
}
